import { useEffect, useState } from "react";
import { effectiveTheme, readStoredTheme, writeStoredTheme, type ThemeChoice } from "./theme";

/**
 * Spec §3.3: drive the console color scheme from a single theme attribute on
 * the document root, so light / dark styles can be applied via CSS without
 * React re-renders.
 */
export function useTheme(): {
  theme: ThemeChoice;
  effective: "light" | "dark";
  setTheme: (next: ThemeChoice) => void;
} {
  const [theme, setThemeState] = useState<ThemeChoice>(() => readStoredTheme());
  const [effective, setEffective] = useState<"light" | "dark">(() =>
    effectiveTheme(readStoredTheme()),
  );

  useEffect(() => {
    function apply() {
      const value = effectiveTheme(theme);
      document.documentElement.dataset["theme"] = value;
      setEffective(value);
    }
    apply();

    if (theme !== "system") return;

    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => apply();
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [theme]);

  function setTheme(next: ThemeChoice) {
    writeStoredTheme(next);
    setThemeState(next);
  }

  return { theme, effective, setTheme };
}
